import React, { useContext } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import set from 'lodash/set';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import { getSelectorOptions } from 'src/presentation/components/pages/shared/utils/Utils';
import {
  ALERT_COLOR_BLOCK_INPUTS,
  BORDER_RADIUS,
  BORDER_SIZE,
  COLORS_BLOCK_INPUTS,
  DISPLAY_ALIGNMENT_BLOCK_INPUTS,
  DROPDOWN_SHADOW_AND_BORDER_BLOCK_INPUTS,
  FIELDS_MAPPER,
  ON_OR_OFF,
  SDK_STYLE_DESCRIPTION,
  MOBILE_SDK_STYLE_DESCRIPTION,
  MOBILE_ALERT_COLOR_BLOCK_INPUTS,
  WIDGET_ALIGNMENT,
} from '../chatConstants';

import {
  ColumnRow,
  CustomRow,
  DefaultSection,
  FixedWidthContentRow,
  HalfRow,
  HeaderPosition,
  InputsBlock,
  InputType,
  MediumHeader,
  PrimaryDescription,
  PrimaryHeader,
  SelectorContainer,
  StyledImage,
} from 'src/presentation/components/pages/chat/ChatLayout';

import { DropdownContainer } from 'src/presentation/components/pages/shared/HoursTable/HoursTable';
import { InputSize } from 'src/presentation/components/forms/InputSize';
import Checkbox from 'src/presentation/components/forms/Checkbox';
import DisplayBottomAlignment from 'src/images/DisplayBottomAlignment.svg';
import DropShadowBorder from 'src/images/DropShadowBorder.svg';
import MobileDropShadowBorder from 'src/images/MobileDropShadowBorder.svg';
import ChatBubbles from 'src/images/ChatBubbles.svg';
import MobileBorderRadius from 'src/images/MobileBorderRadius.svg';
import MobileChatBubbles from 'src/images/MobileChatBubble.svg';
import MobilePoweredByGladlyAndPrivacyDisclaimer from 'src/images/MobilePoweredByGladlyAndPrivacyDisclaimer.svg';
import PoweredByGladlyAndPrivacyDisclaimer from 'src/images/PoweredByGladlyAndPrivacyDisclaimer.svg';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import HtmlEditor from '../components/HtmlEditor';
import PrivacyPolicyEditor from '../components/PrivacyPolicyEditor';

const ChatStyleSection = () => {
  const { workingState, updateState } = useContext(FormContext);
  const address = get(workingState, 'address');
  const sectionHeaderLabel = `Style ${address}`;
  const isFeatureEnabled = useIsFeatureEnabled();
  const isHtmlEditorEnabled = isFeatureEnabled('gladAppHtmlEditor');

  return (
    <>
      <PrimaryHeader>{sectionHeaderLabel}</PrimaryHeader>
      <PrimaryDescription>{SDK_STYLE_DESCRIPTION}</PrimaryDescription>
      <MediumHeader>Placement</MediumHeader>
      {renderDisplayAlignment({ workingState })}
      {renderPrivacyPolicyAndPoweredByGladly({ workingState, updateState, isHtmlEditorEnabled })}
      {renderDropShadowAndBorderBlock({ workingState })}
      {renderColorsBlock({ workingState })}
      {renderAlertColorBlock({ workingState }, isFeatureEnabled)}
      {renderEnableAttachments({ workingState })}
    </>
  );
};

export const MobileChatStyleSection = () => {
  const { workingState, updateState } = useContext(FormContext);
  const address = get(workingState, 'address');
  const sectionHeaderLabel = `Style ${address}`;
  const isFeatureEnabled = useIsFeatureEnabled();
  const isHtmlEditorEnabled = isFeatureEnabled('gladAppHtmlEditor');

  return (
    <>
      <PrimaryHeader>{sectionHeaderLabel}</PrimaryHeader>
      <PrimaryDescription>{MOBILE_SDK_STYLE_DESCRIPTION}</PrimaryDescription>

      {renderPrivacyPolicyAndPoweredByGladly({ workingState, updateState, isMobile: true, isHtmlEditorEnabled })}
      {renderDropShadowAndBorderBlock({ workingState, isMobile: true })}
      {renderColorsBlock({ workingState, isMobile: true })}
      {renderAlertColorBlock({ workingState, isMobile: true }, isFeatureEnabled)}
    </>
  );
};

function renderSwitcherWithBox({ attr, label, description, options }) {
  return (
    <DropdownContainer>
      <Dropdown
        attr={attr}
        data-aid={attr}
        label={label}
        description={description}
        options={getSelectorOptions(options)}
      />
    </DropdownContainer>
  );
}

const renderDisplayAlignment = ({ workingState }) => {
  return (
    <DefaultSection svgImage={DisplayBottomAlignment} headerPosition={HeaderPosition.INSIDE_BLOCK}>
      <CustomRow>
        <SelectorContainer>
          <RadioGroup
            attr={FIELDS_MAPPER.ALIGNMENT.path}
            data-aid={FIELDS_MAPPER.ALIGNMENT.path}
            description={FIELDS_MAPPER.ALIGNMENT.description}
            label="Display bottom alignment"
            options={getSelectorOptions(WIDGET_ALIGNMENT)}
          />
        </SelectorContainer>
      </CustomRow>
      {InputsBlock({
        workingState,
        size: InputSize.LARGE,
        inputType: InputType.NUMBER,
        controlsList: DISPLAY_ALIGNMENT_BLOCK_INPUTS,
      })}
    </DefaultSection>
  );
};

const getUIParams = isMobile => {
  if (isMobile) {
    return {
      svgImage: MobilePoweredByGladlyAndPrivacyDisclaimer,
      gladlyDescription: FIELDS_MAPPER.POWERED_BY_GLADLY.mobileDescription,
      privacyDescription: FIELDS_MAPPER.PRIVACY_DISCLAIMER_IS_ACTIVATED.mobileDescription,
    };
  }

  return {
    svgImage: PoweredByGladlyAndPrivacyDisclaimer,
    gladlyDescription: FIELDS_MAPPER.POWERED_BY_GLADLY.description,
    privacyDescription: FIELDS_MAPPER.PRIVACY_DISCLAIMER_IS_ACTIVATED.description,
  };
};

const renderPrivacyPolicyAndPoweredByGladly = ({
  workingState,
  updateState,
  isMobile = false,
  isHtmlEditorEnabled,
}) => {
  const privacyDisclaimerEnabled = get(workingState, FIELDS_MAPPER.PRIVACY_DISCLAIMER_IS_ACTIVATED.path, false);
  const poweredByGladlyEnabled = get(workingState, FIELDS_MAPPER.POWERED_BY_GLADLY.path, false);

  const togglePrivacyDisclaimerEnabled = ({ checked }) => {
    let changes = set({}, FIELDS_MAPPER.PRIVACY_DISCLAIMER_IS_ACTIVATED.path, checked);
    if (!checked) {
      changes = set(changes, FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path, '');
    }
    updateState(changes, { replaceArrayValues: true });
  };

  const togglePoweredByGladly = ({ checked }) => {
    const changes = set({}, FIELDS_MAPPER.POWERED_BY_GLADLY.path, checked);
    updateState(changes, { replaceArrayValues: true });
  };

  const uiParams = getUIParams(isMobile);

  return (
    <DefaultSection svgImage={uiParams.svgImage} imageMargin={16} headerPosition={HeaderPosition.INSIDE_BLOCK}>
      <CustomRow>
        <CheckboxContainer>
          <Checkbox
            checked={!!poweredByGladlyEnabled}
            label={<CheckboxLabel label="Powered by Gladly" />}
            attr={FIELDS_MAPPER.POWERED_BY_GLADLY.path}
            data-aid={FIELDS_MAPPER.POWERED_BY_GLADLY.path}
            onChange={() => togglePoweredByGladly({ checked: !poweredByGladlyEnabled })}
            value={poweredByGladlyEnabled ? 'checked' : ''}
          />
          <CheckboxInfo>{uiParams.gladlyDescription}</CheckboxInfo>
        </CheckboxContainer>
      </CustomRow>
      <CustomRow>
        <CheckboxContainer>
          <Checkbox
            attr={FIELDS_MAPPER.PRIVACY_DISCLAIMER_IS_ACTIVATED.path}
            checked={!!privacyDisclaimerEnabled}
            data-aid={FIELDS_MAPPER.PRIVACY_DISCLAIMER_IS_ACTIVATED.path}
            label={<CheckboxLabel label="Privacy policy disclaimer" />}
            onChange={() => togglePrivacyDisclaimerEnabled({ checked: !privacyDisclaimerEnabled })}
            value={privacyDisclaimerEnabled ? 'checked' : ''}
          />
          <CheckboxInfo $moreSpaceBelow={privacyDisclaimerEnabled}>{uiParams.privacyDescription}</CheckboxInfo>
          {privacyDisclaimerEnabled ? (
            isHtmlEditorEnabled ? (
              <HtmlEditor editorPath={FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path} placeholder={'Enter Text...'} />
            ) : (
              <PrivacyPolicyEditor />
            )
          ) : null}
        </CheckboxContainer>
      </CustomRow>
    </DefaultSection>
  );
};

const renderEnableAttachments = ({ workingState }) => {
  const attachmentsEnabled = get(workingState, FIELDS_MAPPER.ENABLE_ATTACHMENTS.path, true);

  return (
    <DefaultSection headerPosition={HeaderPosition.INSIDE_BLOCK}>
      <CustomRow>
        <CheckboxContainer>
          <Checkbox
            checked={!!attachmentsEnabled}
            label={<CheckboxLabel label="Enable attachments" />}
            attr={FIELDS_MAPPER.ENABLE_ATTACHMENTS.path}
            data-aid={FIELDS_MAPPER.ENABLE_ATTACHMENTS.path}
            value={attachmentsEnabled ? 'checked' : ''}
          />
          <CheckboxInfo>{FIELDS_MAPPER.ENABLE_ATTACHMENTS.description}</CheckboxInfo>
        </CheckboxContainer>
      </CustomRow>
    </DefaultSection>
  );
};

const CheckboxLabel = ({ label }) => {
  return <CheckboxLabelText>{label}</CheckboxLabelText>;
};

const CheckboxLabelText = styled.span`
  font-weight: bold;
  display: flex;
`;

const CheckboxInfo = styled.span`
  display: flex;
  line-height: 1.6;
  padding-left: 24px;
  position: relative;
  top: 5px;
  color: ${p => p.theme.colors.gray700};
  ${p => (p.$moreSpaceBelow ? 'margin-bottom: 8px;' : '')}
`;

const CheckboxContainer = styled.span`
  width: ${InputSize.XLARGE};
`;

const renderDropShadowAndBorderBlock = ({ workingState, isMobile = false }) => {
  const borderWidth = get(workingState, FIELDS_MAPPER.BORDER_WIDTH.path);
  const borderWidthEnabled = borderWidth !== '0';
  return (
    <DefaultSection
      svgImage={!isMobile && DropShadowBorder}
      headerPosition={HeaderPosition.INSIDE_BLOCK}
      header={`${isMobile ? 'Border style' : ''}`}
    >
      {!isMobile && (
        <CustomRow>
          <RadioGroup
            attr={FIELDS_MAPPER.THEME_DROPSHADOW_ENABLED.path}
            data-aid={FIELDS_MAPPER.THEME_DROPSHADOW_ENABLED.path}
            label="Drop shadow"
            options={getSelectorOptions(ON_OR_OFF)}
          />
        </CustomRow>
      )}
      {isMobile ? (
        <FixedWidthContentRow>
          <ColumnRow>
            {renderSwitcherWithBox({
              attr: FIELDS_MAPPER.BORDER_WIDTH.path,
              label: 'Border width',
              description: '',
              options: BORDER_SIZE,
            })}
            {borderWidthEnabled &&
              InputsBlock({
                workingState,
                size: InputSize.LARGE,
                inputType: InputType.COLOR_PICKER,
                controlsList: DROPDOWN_SHADOW_AND_BORDER_BLOCK_INPUTS,
              })}
          </ColumnRow>
          <ColumnRow>
            <StyledImage src={MobileDropShadowBorder} />
          </ColumnRow>
        </FixedWidthContentRow>
      ) : (
        <>
          {' '}
          <CustomRow>
            {renderSwitcherWithBox({
              attr: FIELDS_MAPPER.BORDER_WIDTH.path,
              label: 'Border width',
              description: '',
              options: BORDER_SIZE,
            })}
          </CustomRow>
          {borderWidthEnabled &&
            InputsBlock({
              workingState,
              size: InputSize.LARGE,
              inputType: InputType.COLOR_PICKER,
              controlsList: DROPDOWN_SHADOW_AND_BORDER_BLOCK_INPUTS,
            })}
        </>
      )}
      {isMobile ? (
        <CustomRow>
          <HalfRow>
            {renderSwitcherWithBox({
              attr: FIELDS_MAPPER.BORDER_RADIUS.path,
              label: 'Border radius',
              description: '',
              options: BORDER_RADIUS,
            })}
          </HalfRow>
          <HalfRow>
            <StyledImage src={MobileBorderRadius} />
          </HalfRow>
        </CustomRow>
      ) : (
        <CustomRow>
          {renderSwitcherWithBox({
            attr: FIELDS_MAPPER.BORDER_RADIUS.path,
            label: 'Border radius',
            description: '',
            options: BORDER_RADIUS,
          })}
        </CustomRow>
      )}
    </DefaultSection>
  );
};

const renderColorsBlock = ({ workingState, isMobile = false }) => {
  const svgImage = isMobile ? MobileChatBubbles : ChatBubbles;

  return (
    <DefaultSection svgImage={svgImage} headerPosition={HeaderPosition.INSIDE_BLOCK} header="Colors" description="">
      {InputsBlock({
        workingState,
        size: InputSize.LARGE,
        inputType: InputType.COLOR_PICKER,
        controlsList: COLORS_BLOCK_INPUTS,
      })}
    </DefaultSection>
  );
};

function renderAlertColorBlock({ workingState, isMobile = false }, isFeatureEnabled) {
  const alertColorBlockInputs = isMobile ? MOBILE_ALERT_COLOR_BLOCK_INPUTS : ALERT_COLOR_BLOCK_INPUTS;

  return alertColorBlockInputs.map(input => {
    if (input.featureFlag && !isFeatureEnabled(input.featureFlag)) {
      return null;
    }
    return (
      <FixedWidthContentRow key={input.attr}>
        <HalfRow>
          {InputsBlock({
            workingState,
            size: InputSize.LARGE,
            inputType: InputType.COLOR_PICKER,
            controlsList: [input],
          })}
        </HalfRow>
        <HalfRow>
          <StyledImage src={input.svgImage} />
        </HalfRow>
      </FixedWidthContentRow>
    );
  });
}

export default ChatStyleSection;
